
import { toast } from 'react-hot-toast';
import { WorkflowStep, CampaignFormData } from '../../types';
import { updateCampaignData } from '../../utils/firebase';
import { callGemini } from '../../utils/gemini';
import { populatePlayingToWinTemplate, playingToWinTemplates } from '../../utils/templates/playingToWinTemplates';
import { checkDependencies, getPreviousResults } from './workflowDependencies';
import { sanitizeForSerialization } from '../../utils/dataSerializer';

interface ProcessStepParams {
  campaignId: string;
  currentStep: number;
  steps: WorkflowStep[];
  formData: CampaignFormData;
  retryCount: number;
  maxRetries: number;
  retryDelay: number;
}

interface ProcessStepResult {
  success: boolean;
  updatedSteps?: WorkflowStep[];
  nextStep?: number;
  isComplete?: boolean;
  shouldRetry?: boolean;
  error?: string;
}

export const processWorkflowStepSimple = async (params: ProcessStepParams): Promise<ProcessStepResult> => {
  const { campaignId, currentStep, steps, formData, retryCount, maxRetries } = params;

  console.log('🚀 STARTING processWorkflowStepSimple:', {
    campaignId,
    currentStep,
    stepsLength: steps.length,
    retryCount,
    maxRetries,
    timestamp: new Date().toISOString()
  });

  console.log('📋 Form data keys:', Object.keys(formData));
  console.log('📋 Steps overview:', steps.map(s => ({ id: s.id, title: s.title, status: s.status })));

  console.log('🔍 Checking dependencies for step:', currentStep);
  if (!checkDependencies(currentStep, steps)) {
    console.log('❌ Dependencies not met for step:', currentStep);
    return { success: false, error: 'Dependencies not met' };
  }
  console.log('✅ Dependencies satisfied for step:', currentStep);

  try {
    console.log('🔍 Finding step data for step:', currentStep);
    const step = steps.find(s => s.id === currentStep);
    if (!step) {
      console.error('❌ Step not found:', currentStep);
      console.error('Available steps:', steps.map(s => ({ id: s.id, title: s.title })));
      throw new Error('Step not found');
    }

    console.log('📝 Found step:', { id: step.id, title: step.title, template: step.template });

    // Get the actual template content from the templates object
    const templateKey = step.template.replace('playingToWinTemplates.', '');
    console.log('🎯 Looking for template key:', templateKey);
    console.log('🎯 Available templates:', Object.keys(playingToWinTemplates));
    
    const actualTemplate = playingToWinTemplates[templateKey as keyof typeof playingToWinTemplates];
    
    if (!actualTemplate) {
      console.error('❌ Template not found:', templateKey);
      console.error('Available templates:', Object.keys(playingToWinTemplates));
      throw new Error(`Template not found: ${templateKey}`);
    }

    console.log(`✅ Using template: ${templateKey}`);

    console.log('📊 Getting previous results for step:', currentStep);
    const previousResults = getPreviousResults(currentStep, steps);
    console.log('📊 Previous results keys:', previousResults ? Object.keys(previousResults) : 'null');
    
    console.log('📝 Populating template with data...');
    const prompt = populatePlayingToWinTemplate(actualTemplate, formData, previousResults);
    
    console.log('✅ Generated prompt for step:', currentStep);
    console.log('📝 Prompt length:', prompt.length);
    console.log('📝 Prompt preview (first 500 chars):', prompt.substring(0, 500) + '...');

    // Check for API key in localStorage first, then env
    let apiKey = localStorage.getItem('gemini_api_key');
    if (!apiKey) {
      apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    }
    
    if (!apiKey) {
      console.error('❌ No Gemini API key found in localStorage or environment');
      throw new Error('Gemini API key is not configured. Please add your key in the application settings.');
    }

    console.log('🤖 Calling Gemini API...');
    const response = await callGemini(prompt, apiKey);

    if (response.error) {
      console.error('❌ Gemini API returned error:', response.error);
      throw new Error(response.error);
    }

    console.log('✅ Gemini API response received');
    console.log('📝 Response length:', response.content.length);
    console.log('📝 Response preview:', response.content.substring(0, 200) + '...');

    console.log('💾 Updating step status to completed...');
    const updatedSteps = steps.map(s =>
      s.id === currentStep 
        ? { ...s, status: 'completed' as const, result: response.content }
        : s
    );

    const isComplete = currentStep === steps.length;
    const nextStep = isComplete ? currentStep : currentStep + 1;

    console.log(`🎯 Step processing result:`, {
      currentStep,
      nextStep: isComplete ? 'COMPLETE' : nextStep,
      isComplete,
      totalSteps: steps.length
    });

    // Simple analysis state for Firebase - sanitize before saving
    console.log('💾 Preparing analysis state for Firebase...');
    const analysisState = {
      steps: updatedSteps,
      currentStep: nextStep,
      isComplete,
      stepResults: updatedSteps.reduce((acc, step) => ({
        ...acc,
        [step.id]: step.result
      }), {}),
      lastUpdated: new Date().toISOString()
    };

    // Sanitize analysis state to prevent DataCloneError
    console.log('🧹 Sanitizing analysis state for serialization...');
    const sanitizedState = sanitizeForSerialization(analysisState);
    
    // Validate data before saving
    console.log('🔍 Validating analysis state for serialization...');
    try {
      JSON.stringify(sanitizedState);
      console.log('✅ Analysis state validation passed');
    } catch (validationError) {
      console.error('❌ Analysis state serialization failed:', validationError);
      // Don't throw error - continue with workflow
      console.warn('⚠️ Continuing without saving state due to serialization issues');
    }

    console.log('💾 Saving analysis state to Firebase...');
    console.log('📊 Analysis state summary:', {
      stepCount: sanitizedState.steps?.length || 0,
      currentStep: sanitizedState.currentStep,
      isComplete: sanitizedState.isComplete,
      resultsCount: Object.keys(sanitizedState.stepResults || {}).length
    });

    try {
      await updateCampaignData(campaignId, { analysisState: sanitizedState });
      console.log('✅ Analysis state saved successfully');
    } catch (updateError) {
      console.error('❌ Failed to save analysis state:', updateError);
      toast.error('Warning: Progress may not be saved due to connection issues');
      // Continue execution - we have the data in memory
    }

    if (isComplete) {
      console.log('🎉 Analysis complete!');
      toast.success('Political campaign analysis complete!');
    }

    console.log('✅ processWorkflowStepSimple completed successfully');
    return {
      success: true,
      updatedSteps,
      nextStep,
      isComplete
    };

  } catch (error) {
    console.error('❌ Error processing step:', error);
    console.error('Error details:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });
    
    if (retryCount < maxRetries) {
      console.log(`🔄 Retrying step ${currentStep}, attempt ${retryCount + 1}/${maxRetries}`);
      return {
        success: false,
        shouldRetry: true,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } else {
      console.log(`🚫 Max retries exceeded for step ${currentStep}`);
      const errorSteps = steps.map(s =>
        s.id === currentStep ? { ...s, status: 'error' as const } : s
      );
      
      toast.error(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      return {
        success: false,
        updatedSteps: errorSteps,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
};

export const createRestartAnalysisStateSimple = (steps: WorkflowStep[]) => {
  const restartSteps = steps.map(step => ({
    ...step,
    status: 'pending' as const,
    result: ''
  }));

  return {
    steps: restartSteps,
    currentStep: 1,
    isComplete: false,
    stepResults: {},
    consolidatedReport: null
  };
};
